import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Loader2, Heart, MessageCircle, UserPlus, UserCheck, Plus, X, Sparkles, Send, ShieldCheck, Image as ImageIcon, ThumbsUp, FileText, ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { ConnectButton } from "@/components/ConnectButton";
import { CheckinBar } from "@/components/CheckinBar";
import { StreakLeaderboard } from "@/components/StreakLeaderboard";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "professional", label: "Professional" },
  { id: "business", label: "Business" },
  { id: "talent", label: "Talent" },
];

function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v") || u.pathname.split("/").pop();
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host.endsWith("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").pop()}`;
    return null;
  } catch { return null; }
}

export default function Showcase() {
  const { user } = useApp();
  const [cat, setCat] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async (c = cat) => {
    setLoading(true);
    try { const { data } = await api.get(`/showcase${c && c !== "all" ? `?category=${c}` : ""}`); setPosts(data.posts || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(cat); /* eslint-disable-next-line */ }, [cat]);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Users size={14} /> Community</span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Share your work & talent</h1>
          <p className="text-sec mt-3">Professionals, businesses and creators share their craft. Follow, appreciate and comment — with kindness. My Kind gently reduces the reach of abusive profiles and coaches a better tone.</p>
        </div>
        {user && (
          <button data-testid="showcase-new" onClick={() => setShowForm(true)} className="btn-primary h-11 px-5 flex items-center gap-2"><Plus size={16} /> Share your work</button>
        )}
      </div>

      <div className="flex items-center gap-2 rounded-xl bg-brand/10 border border-brand/30 p-3 text-xs mt-5">
        <ShieldCheck size={16} className="text-brand shrink-0" />
        <span className="text-[var(--rud-text)]">A kind, safe space — no abuse, hate or discrimination on race, colour, gender, age or nationality. Everyone is one family.</span>
      </div>

      <div className="flex gap-1 p-1 rounded-full bg-elevated border border-rud w-fit mt-6">
        {CATEGORIES.map((c) => (
          <button key={c.id} data-testid={`showcase-cat-${c.id}`} onClick={() => setCat(c.id)} className={`px-5 py-1.5 rounded-full text-sm transition-colors ${cat === c.id ? "bg-brand text-black" : "text-sec"}`}>{c.label}</button>
        ))}
      </div>

      {!user && (
        <div className="rud-card rounded-2xl p-6 mt-6 text-center">
          <p className="text-sm text-sec">Sign in to share your work, follow creators and comment.</p>
          <a href="/auth" className="btn-primary h-10 px-6 inline-flex items-center mt-3 text-sm">Sign in</a>
        </div>
      )}

      {user && <CheckinBar onPosted={() => load(cat)} />}
      {user && <StreakLeaderboard />}

      {loading ? (
        <div className="mt-10 text-center text-sec"><HeartLoader /></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {posts.map((p) => <PostCard key={p.id} post={p} me={user} onChange={() => load(cat)} />)}
          {posts.length === 0 && <div className="col-span-2 text-center text-sec text-sm py-10">No posts yet. Be the first to share your work.</div>}
        </div>
      )}

      <AnimatePresence>
        {showForm && <PostForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(cat); }} />}
      </AnimatePresence>
    </div>
  );
}

export function PostCard({ post, me, onChange }) {
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [loveCount, setLoveCount] = useState(post.love_count || 0);
  const [following, setFollowing] = useState(post.is_following_author);
  const [openComments, setOpenComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [cText, setCText] = useState("");
  const [busy, setBusy] = useState(false);

  const react = async (kind) => {
    if (!me) { toast.error("Sign in to react."); return; }
    try {
      const { data } = await api.post(`/showcase/${post.id}/react?kind=${kind}`);
      setMyReaction(data.my_reaction); setLikeCount(data.like_count); setLoveCount(data.love_count);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const follow = async () => {
    if (!me) { toast.error("Sign in to follow."); return; }
    try { const { data } = await api.post(`/users/${post.user_id}/follow`); setFollowing(data.following); toast(data.following ? "Following" : "Unfollowed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const loadComments = async () => {
    setOpenComments((v) => !v);
    if (!openComments) { try { const { data } = await api.get(`/showcase/${post.id}/comments`); setComments(data.comments || []); } catch {} }
  };
  const comment = async () => {
    if (!me) { toast.error("Sign in to comment."); return; }
    if (!cText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/showcase/${post.id}/comment`, { body: cText });
      setComments((c) => [data.comment, ...c]);
      setCText("");
      if (data.moderation?.recommendation) toast.warning("Tone check", { description: data.moderation.recommendation });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rud-card rounded-2xl overflow-hidden" data-testid="showcase-post">
      {post.media_url && (post.media_type === "video"
        ? (toEmbed(post.media_url)
            ? <div className="w-full aspect-video bg-black"><iframe src={toEmbed(post.media_url)} title={post.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
            : <video src={post.media_url} controls className="w-full h-48 object-cover bg-black" />)
        : (post.media_type === "link" || post.media_type === "document")
          ? <a href={post.media_url} target="_blank" rel="noopener noreferrer" data-testid="post-open-link" className="flex items-center gap-2 bg-elevated border-b border-rud px-4 py-3 text-sm hover:text-brand transition-colors"><FileText size={16} className="text-brand" /> Open {post.media_type === "document" ? "document" : "link"} <ArrowUpRight size={14} className="ml-auto" /></a>
          : <img src={post.media_url} alt="" className="w-full h-48 object-cover" />)}
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-brand text-black grid place-items-center text-sm font-bold">{(post.author_name || "?")[0].toUpperCase()}</div>
            <div>
              <div className="text-sm font-medium leading-none">{post.author_name}</div>
              <div className="text-[11px] text-sec capitalize">{post.category}</div>
            </div>
          </div>
          {!post.is_mine && <ConnectButton userId={post.user_id} size="xs" />}
        </div>
        <h3 className="font-serif text-2xl mt-3 leading-tight">{post.title}</h3>
        {post.body && <p className="text-sm text-sec mt-1.5 leading-relaxed whitespace-pre-wrap">{post.body}</p>}
        {post.flagged && <div className="text-[11px] text-yellow-400 mt-2">Reduced reach — flagged for tone.</div>}

        <div className="flex items-center gap-4 mt-4 text-sm text-sec">
          <button data-testid="showcase-like" onClick={() => react("like")} className={`flex items-center gap-1.5 transition-colors ${myReaction === "like" ? "text-brand" : "hover:text-brand"}`}>
            <ThumbsUp size={16} className={myReaction === "like" ? "fill-current" : ""} /> {likeCount}
          </button>
          <button data-testid="showcase-love" onClick={() => react("love")} className={`flex items-center gap-1.5 transition-colors ${myReaction === "love" ? "text-red-400" : "hover:text-red-400"}`}>
            <Heart size={16} className={myReaction === "love" ? "fill-current" : ""} /> {loveCount}
          </button>
          <button data-testid="showcase-comment-toggle" onClick={loadComments} className="flex items-center gap-1.5 hover:text-brand">
            <MessageCircle size={16} /> {post.comment_count}
          </button>
        </div>

        {openComments && (
          <div className="mt-4 space-y-3">
            {me && (
              <div className="flex gap-2">
                <input data-testid="showcase-comment-input" value={cText} onChange={(e) => setCText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && comment()} placeholder="Add a kind comment…" className="rud-input flex-1 rounded-full px-3 py-2 text-sm" />
                <button data-testid="showcase-comment-send" onClick={comment} disabled={busy} className="btn-primary h-9 w-9 grid place-items-center disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}</button>
              </div>
            )}
            {comments.map((c) => (
              <div key={c.id} className="rounded-lg bg-elevated border border-rud p-2.5">
                <div className="text-xs font-medium">{c.author_name} {c.flagged && <span className="text-yellow-400">· low reach</span>}</div>
                <div className="text-sm text-sec">{c.body}</div>
              </div>
            ))}
            {comments.length === 0 && <div className="text-xs text-sec text-center py-2">No comments yet.</div>}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function PostForm({ onClose, onDone, communityId = null }) {
  const [f, setF] = useState({ title: "", body: "", category: "professional", media_url: "", media_type: "image" });
  const [busy, setBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const generate = async () => {
    if (aiPrompt.trim().length < 3) { toast.error("Tell me what you'd like to post about."); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/showcase/ai-draft", { prompt: aiPrompt.trim(), category: f.category });
      setF((prev) => ({ ...prev, title: data.title || prev.title, body: data.body || prev.body }));
      toast.success("Draft ready — review, tweak, and post 🌿");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Add a title."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/showcase", { ...f, community_id: communityId });
      if (data.moderation?.recommendation) toast.warning("Tone check", { description: data.moderation.recommendation });
      else toast.success(communityId ? "Posted to the community!" : "Shared with the community!");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <motion.div initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} className="rud-card rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="showcase-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl flex items-center gap-2"><Sparkles size={18} className="text-brand" /> Share your work</h3>
          <button onClick={onClose} className="text-sec hover:text-[var(--rud-text)]"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div className="rud-card rounded-xl p-3 border border-brand/30" data-testid="ai-post-helper">
            <div className="text-xs uppercase tracking-widest text-brand flex items-center gap-1.5 mb-2"><Sparkles size={13} /> AI — Write my personalised post</div>
            <textarea data-testid="ai-post-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={2}
              placeholder="What do you want to post about? e.g. 'I finished my first marathon for a cause' — I'll write it in your voice." className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />
            <button data-testid="ai-post-generate" onClick={generate} disabled={aiBusy}
              className="btn-primary rounded-full h-9 px-4 text-xs mt-2 flex items-center gap-1.5 disabled:opacity-60">
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate my post
            </button>
            <p className="text-[10px] text-sec mt-1.5">Shaped by your profile, interests &amp; tone. Review &amp; edit below before posting.</p>
          </div>
          <input data-testid="post-title" value={f.title} onChange={set("title")} placeholder="Title of your work / talent" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <textarea data-testid="post-body" value={f.body} onChange={set("body")} rows={4} placeholder="Describe it, share your story…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none" />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="post-category" value={f.category} onChange={set("category")} className="rud-input rounded-lg px-3 py-2.5 text-sm">
              <option value="professional">Professional</option><option value="business">Business</option><option value="talent">Talent</option>
            </select>
            <select data-testid="post-media-type" value={f.media_type} onChange={set("media_type")} className="rud-input rounded-lg px-3 py-2.5 text-sm">
              <option value="image">Photo</option><option value="video">Video / YouTube</option><option value="link">Link / Article</option><option value="document">PDF / PPT / Doc</option>
            </select>
          </div>
          <div className="relative">
            <ImageIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="post-media" value={f.media_url} onChange={set("media_url")} placeholder="Paste any URL — photo, YouTube/video, PDF, PPT, doc or article (optional)" className="rud-input w-full rounded-lg pl-9 pr-3 py-2.5 text-sm" />
          </div>
          <button data-testid="post-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Publish
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
